/**
 * Base HTML template generator for bot-rendered pages.
 * Generates SEO-complete HTML with meta tags, JSON-LD, and visible content.
 */

const SITE_URL = 'https://americanhospitalprices.com';
const SITE_NAME = 'American Hospital Prices';
const OG_IMAGE = `${SITE_URL}/og-ahp-2026.jpg`;


/** JSON.stringify does not escape '<', so a crafted URL param embedded in a
 *  JSON-LD value (e.g. "</script><script>") could break out of the script
 *  block. Escape < > & to unicode — valid JSON, inert in HTML. */
function jsonLdSafe(v: unknown): string {
  return JSON.stringify(v)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

export interface PageMeta {
  title: string;
  description: string;
  canonicalPath: string;
  ogImage?: string;
  jsonLd?: object | object[];
  /** og:type override (e.g. 'video.other' for lesson pages). Default 'website'. */
  ogType?: string;
  /** twitter:card override (e.g. 'player'). Default 'summary_large_image'. */
  twitterCard?: string;
  /** Raw extra <head> tags (og:video, twitter:player, …), already escaped. */
  extraHead?: string;
  /** robots meta content, e.g. 'noindex, follow'. Omitted -> no robots meta. */
  robots?: string;
}

/**
 * Renders full HTML page with meta tags and body content for bots.
 * Includes the normal SPA script tags so the page can hydrate if JS runs.
 */
export function renderHTML(meta: PageMeta, bodyContent: string, spaScripts: string): string {
  const canonical = `${SITE_URL}${meta.canonicalPath}`;
  const ogImage = meta.ogImage || OG_IMAGE;
  const fullTitle = meta.title.includes(SITE_NAME) ? meta.title : `${meta.title} | ${SITE_NAME}`;

  const jsonLdScript = meta.jsonLd
    ? (Array.isArray(meta.jsonLd)
        ? meta.jsonLd.map(ld => `<script type="application/ld+json">${jsonLdSafe(ld)}</script>`).join('\n')
        : `<script type="application/ld+json">${jsonLdSafe(meta.jsonLd)}</script>`)
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(fullTitle)}</title>
  <meta name="description" content="${escapeAttr(meta.description)}" />
  <link rel="canonical" href="${canonical}" />

  <meta property="og:title" content="${escapeAttr(fullTitle)}" />
  <meta property="og:description" content="${escapeAttr(meta.description)}" />
  <meta property="og:type" content="${meta.ogType || 'website'}" />
  <meta property="og:url" content="${canonical}" />
  <meta property="og:image" content="${ogImage}" />
  <meta property="og:site_name" content="${SITE_NAME}" />

  <meta name="twitter:card" content="${meta.twitterCard || 'summary_large_image'}" />
  <meta name="twitter:site" content="@amhealthprices" />
  <meta name="twitter:title" content="${escapeAttr(fullTitle)}" />
  <meta name="twitter:description" content="${escapeAttr(meta.description)}" />
  <meta name="twitter:image" content="${ogImage}" />
  ${meta.robots ? `<meta name="robots" content="${meta.robots}" />` : ''}
  ${meta.extraHead || ''}

  <meta name="theme-color" content="#B22234" />
  <link rel="icon" href="/consumer/favicon.ico" sizes="any" />
  <link rel="apple-touch-icon" href="/consumer/apple-touch-icon.png" />

  ${jsonLdScript}

  <style>
    body { font-family: 'Instrument Sans', -apple-system, BlinkMacSystemFont, sans-serif; margin: 0; padding: 0; background: #F7F4EF; color: #1a1a1a; }
    .seo-content { max-width: 1200px; margin: 0 auto; padding: 24px 16px; }
    h1 { font-family: 'Instrument Serif', Georgia, serif; color: #2b5e49; font-size: 2rem; margin-bottom: 8px; }
    h2 { font-family: 'Instrument Serif', Georgia, serif; color: #2b5e49; font-size: 1.5rem; }
    a { color: #2b5e49; }
    table { border-collapse: collapse; width: 100%; margin: 16px 0; }
    th, td { padding: 8px 12px; text-align: left; border-bottom: 1px solid #ddd; }
    th { background: #2b5e49; color: white; font-weight: 600; }
    tr:nth-child(even) { background: #f5f5f0; }
    .grade { display: inline-block; padding: 2px 8px; border-radius: 4px; font-weight: 700; color: white; }
    .grade-a { background: #16a34a; }
    .grade-b { background: #65a30d; }
    .grade-c { background: #ca8a04; }
    .grade-d { background: #ea580c; }
    .grade-f { background: #dc2626; }
    .stat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; margin: 16px 0; }
    .stat-card { background: white; padding: 16px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    .stat-value { font-size: 1.5rem; font-weight: 700; color: #2b5e49; }
    .stat-label { font-size: 0.875rem; color: #666; }
    .nav-links { background: #2b5e49; padding: 12px 16px; }
    .nav-links a { color: white; margin-right: 16px; text-decoration: none; font-size: 0.875rem; }
  </style>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&family=Instrument+Serif:ital,wght@0,400;1,400&display=swap" rel="stylesheet">
</head>
<body>
  <nav class="nav-links">
    <a href="/">Home</a>
    <a href="/search">Search</a>
    <a href="/scenarios">Care Scenarios</a>
    <a href="/classroom">Classroom</a>
    <a href="/hospital-rankings">Hospital Rankings</a>
    <a href="/state-comparison">State Comparison</a>
    <a href="/cpt">CPT Codes</a>
    <a href="/hcpcs">HCPCS Codes</a>
    <a href="/drg">DRG Codes</a>
    <a href="/revenue">Revenue Codes</a>
    <a href="/drugs">Drug Prices</a>
    <a href="/common-procedures">Common Procedures</a>
    <a href="/connect-to-care">Connect to Care</a>
  </nav>
  <div class="seo-content">
    ${bodyContent}
  </div>
  <div id="root"></div>
  ${spaScripts}
</body>
</html>`;
}

/** Canonical hospital page path — the same slug rule the hospital page and
 *  sitemap_hospitals.xml use. Link with this, not a bare /hospital/<id>: the bare
 *  form is a duplicate URL Google has to crawl and dedupe (crawl audit 2026-10-22). */
export function hospitalPath(id: string, name: string | null | undefined): string {
  const slug = (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  return slug ? `/hospital/${id}/${slug}` : `/hospital/${id}`;
}

export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function escapeAttr(str: string): string {
  return str.replace(/"/g, '&quot;').replace(/&/g, '&amp;');
}

export function getLetterGrade(markup: number | null): string {
  // Hospital-level scale for the median-baseline metric (matches
  // HOSPITAL_GRADE_BOUNDARIES in src/lib/grades.ts). All bot-SSR grades are
  // hospital-level, so this is the only scale needed here.
  if (markup === null || markup === undefined) return 'N/A';
  if (markup <= -45) return 'A';
  if (markup <= 20) return 'B';
  if (markup <= 90) return 'C';
  if (markup <= 175) return 'D';
  return 'F';
}

export function getGradeClass(grade: string): string {
  const map: Record<string, string> = { A: 'grade-a', B: 'grade-b', C: 'grade-c', D: 'grade-d', F: 'grade-f' };
  return map[grade] || '';
}

export function formatPercent(val: number | null): string {
  if (val === null || val === undefined) return 'N/A';
  const sign = val >= 0 ? '+' : '';
  return `${sign}${val.toFixed(1)}%`;
}

export function formatCurrency(val: number | null): string {
  if (val === null || val === undefined) return 'N/A';
  return `$${val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
